import React from 'react';
import StaticPageLayout from '../components/StaticPageLayout';

const SUPPORT_EMAIL = 'support@todaytheapp.com';
const LAST_UPDATED = 'April 17, 2026';

export default function TermsPage() {
  return (
    <StaticPageLayout testId="terms-page">
      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12 sm:py-16">
        <p className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: 'var(--primary)', fontFamily: 'Manrope' }}>
          Legal
        </p>
        <h1 className="text-4xl sm:text-5xl font-bold mb-3 leading-tight" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
          Terms &amp; Conditions
        </h1>
        <p className="text-sm mb-10" style={{ color: 'var(--muted-fg)' }}>
          Last updated: {LAST_UPDATED}
        </p>

        <div className="space-y-8" style={{ color: 'var(--text)', fontFamily: 'Georgia, serif', fontSize: '16px', lineHeight: 1.8 }}>
          <p>
            Welcome to Today: Daily Reset ("<strong>Today</strong>", "the app", "we"). These Terms &amp; Conditions ("Terms") form a binding agreement between you and Today. By creating an account, continuing as a guest, or otherwise using our app or website, you agree to be bound by these Terms. If you do not agree, please do not use the service.
          </p>

          <Section title="1. Eligibility">
            <p>You must be at least 13 years old (or 16 years old in the European Economic Area) to use Today. By using the service you represent that you meet this requirement and that any information you provide is accurate.</p>
          </Section>

          <Section title="2. Your account">
            <p>You are responsible for keeping your account credentials secure and for all activity under your account. Notify us immediately at <a href={`mailto:${SUPPORT_EMAIL}`} className="underline" style={{ color: 'var(--primary)' }}>{SUPPORT_EMAIL}</a> if you suspect unauthorized access.</p>
          </Section>

          <Section title="3. Acceptable use">
            <p className="mb-3">You agree <strong>not</strong> to:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Use the service for any unlawful or harmful purpose.</li>
              <li>Attempt to reverse-engineer, scrape, or otherwise extract data from the app beyond normal use.</li>
              <li>Upload content that is infringing, defamatory, hateful, or violates another person's rights.</li>
              <li>Share your account credentials or create multiple accounts to evade usage limits.</li>
              <li>Interfere with the security, performance, or availability of the service.</li>
            </ul>
          </Section>

          <Section title="4. Subscriptions and purchases">
            <p className="mb-3">Today offers paid one-time unlocks (e.g., your IQ Report) and premium subscriptions that unlock additional features. Pricing is displayed in your local currency at checkout and charged by our payment processor, Razorpay.</p>
            <p className="mb-3"><strong>Auto-renewal:</strong> Subscriptions renew automatically at the end of each billing cycle unless you cancel beforehand. You can cancel any time from the Profile → Premium page; access continues through the end of your paid term.</p>
            <p><strong>Taxes:</strong> All prices are inclusive of applicable taxes unless stated otherwise.</p>
          </Section>

          <Section title="5. Refunds">
            <p className="mb-3">Because most purchases unlock digital content immediately, we generally do <strong>not</strong> offer refunds for one-time unlocks once accessed.</p>
            <p>For subscription charges, you may request a refund within <strong>7 days</strong> of purchase <em>provided you have not materially used the premium features</em>. Refund requests can be sent to <a href={`mailto:${SUPPORT_EMAIL}`} className="underline" style={{ color: 'var(--primary)' }}>{SUPPORT_EMAIL}</a> with your transaction ID. We review each request fairly and approve where the spirit of this clause is met. Refunds, when granted, are processed via Razorpay and typically appear in 5–10 business days.</p>
          </Section>

          <Section title="6. User content">
            <p>You retain ownership of the content you create in Today (mood logs, gratitude entries, doodles, etc.). By using the app you grant us a limited, non-exclusive license to store, process, and display that content solely to provide the service to you. We will never sell your wellness content to third parties.</p>
          </Section>

          <Section title="7. Our intellectual property">
            <p>The Today name, logo, designs, illustrations, written content, and software are owned by us or our licensors and protected by copyright and trademark law. Nothing in these Terms grants you any right to use our branding for your own purposes.</p>
          </Section>

          <Section title="8. Wellness disclaimer">
            <p>Today is a general-purpose wellness and self-reflection tool. The content, personality reports, and recommendations provided are for <strong>informational and educational purposes only</strong> and are <strong>not</strong> medical advice, a diagnosis, or therapy. If you are in crisis or experiencing mental-health symptoms, please contact a qualified professional or local emergency services.</p>
          </Section>

          <Section title="9. Service availability and changes">
            <p>We aim for high uptime but don't guarantee uninterrupted service. We may update, add, or remove features over time. We'll give reasonable notice for any change that materially reduces your paid benefits.</p>
          </Section>

          <Section title="10. Limitation of liability">
            <p>To the fullest extent allowed by law, Today and its team are not liable for indirect, incidental, or consequential damages arising from your use of the service. Our aggregate liability in any matter is limited to the amount you paid us in the 12 months before the claim arose.</p>
          </Section>

          <Section title="11. Termination">
            <p>You can delete your account at any time from the Profile page. We may suspend or terminate accounts that violate these Terms. On termination your access ends immediately; we will handle your data as described in our <a href="/privacy-policy" className="underline" style={{ color: 'var(--primary)' }}>Privacy Policy</a>.</p>
          </Section>

          <Section title="12. Governing law and disputes">
            <p>These Terms are governed by the laws of India, without regard to its conflict-of-law principles. Any dispute will be resolved in the competent courts of Bengaluru, India, unless local consumer-protection law grants you a different venue.</p>
          </Section>

          <Section title="13. Changes to these Terms">
            <p>We may update these Terms from time to time. Material changes will be notified in-app or by email at least 14 days before they take effect. Continued use after the effective date constitutes acceptance.</p>
          </Section>

          <Section title="14. Contact">
            <p>
              Questions about these Terms? Email{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="underline" style={{ color: 'var(--primary)' }}>{SUPPORT_EMAIL}</a>.
            </p>
          </Section>
        </div>
      </div>
    </StaticPageLayout>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="text-xl font-bold mb-3" style={{ fontFamily: 'Manrope', color: 'var(--text)' }}>
        {title}
      </h2>
      <div>{children}</div>
    </section>
  );
}
